<template>
  <div class="bar-chart">
    <ApexChart 
      type="bar" 
      class="apex-chart"
      :height="height" 
      :options="chartOptions" 
      :series="series"  
    />
  </div>
</template>

<script setup>
import ApexChart from 'vue3-apexcharts';
import { computed } from 'vue';
import {
  commonChart,
  commonGrid,
  commonXAxisLabels,
  buildCommonTooltip
} from '@/Modules/Grafer/common/chartDefaults';

const props = defineProps({
  series: {
    type: Array,
    required: true
  },
  categories: {
    type: Array,
    default: () => []
  },
  labelYAxis: {
    type: String,
    required: true
  },
  height: {
    type: Number,
    default: 360
  },
  legendItems: {
    type: Array,
    default: () => []
  },
  colors: {
    type: Array
  },
  horizontal: {
    type: Boolean,
    default: false
  },
  // Series names that should be plotted against a secondary (right-hand) y-axis.
  // Use when one series dwarfs another (e.g. DKK vs hours) so the small one stays visible.
  secondaryAxisSeries: {
    type: Array,
    default: () => []
  },
  labelYAxisSecondary: {
    type: String,
    default: ''
  }
});

const yAxisTitleStyle = { color: '#64748b', fontSize: '13px', fontWeight: 500 };

// Build the y-axis config. Horizontal bars keep the empty default. When some series
// are flagged for a secondary scale, give them their own right-hand axis so a
// small-magnitude series stays readable next to a large one; otherwise fall back to
// the original single axis. The axis title is rendered once per side.
const yaxis = computed(() => {
  if (props.horizontal) return {};

  if (props.secondaryAxisSeries.length) {
    let primaryShown = false;
    let secondaryShown = false;
    return props.series.map((s) => {
      const isSecondary = props.secondaryAxisSeries.includes(s.name);
      const firstOnSide = isSecondary ? !secondaryShown : !primaryShown;
      if (isSecondary) secondaryShown = true; else primaryShown = true;
      return {
        seriesName: s.name,
        opposite: isSecondary,
        show: firstOnSide,
        title: {
          text: firstOnSide ? (isSecondary ? props.labelYAxisSecondary : props.labelYAxis) : undefined,
          style: yAxisTitleStyle
        }
      };
    });
  }

  return {
    title: {
      text: props.labelYAxis,
      style: yAxisTitleStyle
    }
  };
});

const chartOptions = computed(() => ({
  chart: {
    type: 'bar',
    height: props.height,
    ...commonChart
  },
  colors: props.colors,
  plotOptions: {
    bar: {
      horizontal: props.horizontal,
      columnWidth: '70%',
      borderRadius: 4,
      borderRadiusApplication: 'end',
      dataLabels: { position: 'top' }
    }
  },
  dataLabels: { enabled: false },
  stroke: {
    show: true,
    width: 1,
    colors: ['transparent']
  },
  grid: {
    ...commonGrid
  },
  xaxis: {
  categories: props.categories,
  type: 'category',
  axisBorder: { show: false },
  axisTicks: { show: false },
  labels: {
    ...commonXAxisLabels,
    rotate: props.horizontal ? 0 : -45,
    maxHeight: 120,
    formatter: (val) => val?.length > 12 ? val.slice(0, 12) + '...' : val
  },
  ...(props.horizontal ? {
    title: {
      text: props.labelYAxis,
      offsetY: 10,
      style: {
        color: '#64748b',
        fontSize: '13px',
        fontWeight: 500
      }
    }
  } : {})
},

yaxis: yaxis.value,
  tooltip: buildCommonTooltip(props.legendItems, props.colors),
  legend: { show: false },
  states: {
    hover: {
      filter: {
        type: 'lighten',
        value: 0.1
      }
    }
  },
  responsive: [
    {
      breakpoint: 1024,
      options: {
        plotOptions: {
          bar: { columnWidth: '80%' }
        }
      }
    },
    {
      breakpoint: 640,
      options: {
        chart: { height: 300 },
        plotOptions: {
          bar: { columnWidth: '90%' }
        },
        xaxis: {
          labels: {
            rotate: props.horizontal ? 0 : -90,
            maxHeight: 150
          }
        }
      }
    }
  ]
}));


</script>

<style scoped>
.bar-chart {
  position: relative;
}

/* Custom tooltip styles (as before) */
:global(.custom-tooltip) {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 140px;
}

:global(.tooltip-time) {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
  font-size: 13px;
  word-break: break-word;
}

:global(.tooltip-item) {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  font-size: 13px;
}

:global(.tooltip-item:last-child) {
  margin-bottom: 0;
}

:global(.tooltip-dot) {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

:global(.tooltip-label) {
  color: #64748b;
  flex: 1;
}

:global(.tooltip-value) {
  font-weight: 600;
  color: #1e293b;
}
</style>
